import { useMemo, useState } from 'react';

const buildSections = (questionCount, durationMinutes) => [
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#218CD9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
    title: 'General Instructions',
    items: [
      <>The test contains <strong>{questionCount} questions</strong> to be completed in <strong>{durationMinutes} minutes.</strong></>,
      <>Each question carries <strong>1 mark.</strong> There is no negative marking.</>,
      <>All questions are <strong>multiple choice</strong> with only one correct answer.</>,
      <>Questions can be answered <strong>in any order.</strong> Use the navigation panel to move between questions.</>,
    ],
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#218CD9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    title: 'Time Management',
    items: [
      <>A countdown timer will be displayed at the top of your screen.</>,
      <>The test will <strong>automatically submit</strong> when time expires.</>,
      <>You can <strong>submit the test early</strong> if you finish before time runs out.</>,
    ],
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#218CD9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    title: 'Important Guidelines',
    items: [
      <><strong>Do not refresh</strong> the browser or press the back button during the test.</>,
      <>Ensure you have a <strong>stable internet connection</strong> throughout the test.</>,
      <><strong>Do not switch tabs</strong> or open other applications during the test.</>,
      <><strong>Your screen will be recorded</strong> throughout the assessment for monitoring purposes.</>,
      <>If you <strong>switch tabs 3 times</strong>, the assessment will be <strong>automatically submitted</strong> and marked as cheating.</>,
      <>You <strong>must enable full-screen</strong> mode before starting the test.</>,
      <>Copying and pasting is strictly prohibited.</>,
      <>Any <strong>suspicious activity</strong> may lead to <strong>disqualification.</strong></>,
    ],
  },
];

export default function TestInstructions({ onClose, onStartExam, questionCount = 0, durationMinutes = 60 }) {
  const [agreed, setAgreed] = useState(false);
  const sections = useMemo(
    () => buildSections(questionCount || 0, durationMinutes || 60),
    [questionCount, durationMinutes]
  );

  return (
    <div style={styles.overlay}>
      <style>{`
        /* Target ONLY the scrollable body, so it doesn't overlap the header */
        .modal-scroll-area::-webkit-scrollbar {
          width: 17px;
        }
        .modal-scroll-area::-webkit-scrollbar-track {
          background: transparent;
          margin-top: 10px;
          margin-bottom: 10px;
        }
        .modal-scroll-area::-webkit-scrollbar-thumb {
          background-color: #9CA3AF;
          border-radius: 10px;
          border: 4px solid transparent;
          background-clip: content-box;
        }
        .modal-scroll-area::-webkit-scrollbar-thumb:hover {
          background-color: #9CA3AF;
        }
      `}</style>
      
      <div style={styles.modal}>
        {/* Header - Fixed at top */}
        <div style={styles.header}>
          <div style={styles.headerInner}>
            <div style={styles.headerIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#218CD9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </div>
            <div style={styles.headerText}>
              <h2 style={styles.title}>Test Instructions</h2>
              <p style={styles.subtitle}>Please read the following instructions carefully before starting the exam.</p>
            </div>
          </div>
        </div>

        {/* Body content - THE SCROLLABLE PART */}
        <div className="modal-scroll-area" style={styles.body}>
          <div style={styles.sectionsWrapper}>
            {sections.map((section, i) => (
              <div key={i} style={styles.section}>
                <div style={styles.sectionTitle}>
                  {section.icon}
                  <span>{section.title}</span>
                </div>
                <ul style={styles.list}>
                  {section.items.map((item, j) => (
                    <li key={j} style={styles.listItem}>
                      <span style={styles.bullet}>•</span>
                      <span style={styles.listText}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <div style={styles.noticeBanner}>
              <div style={styles.noticeTop}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="1.667" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2.5 }}>
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="8" />
                  <line x1="12" y1="12" x2="12" y2="16" />
                </svg>
                <div style={styles.noticeContent}>
                  <p style={styles.noticeTitle}>Important Notice</p>
                  <p style={styles.noticeText}>
                    Once you start the exam, you cannot pause it. Make sure you have allocated sufficient time and are in a distraction-free environment before proceeding.
                  </p>
                </div>
              </div>
            </div>

            <div style={styles.checkboxBanner}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={e => setAgreed(e.target.checked)}
                  style={styles.checkbox}
                />
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '6px',
                  border: agreed ? 'none' : '1.5px solid #9CA3AF',
                  backgroundColor: agreed ? '#86EFAC' : '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.15s ease',
                }}>
                  {agreed && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <p style={styles.checkboxText}>
                  I have read and understood all the instructions. I agree to comply with the test rules and regulations.
                </p>
              </label>
            </div>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div style={styles.footer}>
          <button onClick={onClose} style={styles.cancelBtn}>Cancel</button>
          <button
            onClick={onStartExam}
            disabled={!agreed}
            style={{
              ...styles.startBtn,
              backgroundColor: agreed ? styles.startBtn.backgroundColor : '#D1D5DB',
              color: agreed ? styles.startBtn.color : '#6B7280',
              opacity: 1,
              cursor: agreed ? 'pointer' : 'not-allowed',
            }}
          >
            Start Exam
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="13 6 19 12 13 18"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    width: '600px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column', // Stack header, body, footer
    boxShadow: '0px 20px 25px -5px rgba(0,0,0,0.1), 0px 10px 10px -5px rgba(0,0,0,0.04)',
    overflow: 'hidden', // Contain the scrolling body
  },
  header: {
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 4px 6px rgba(0,0,0,0.08)',
    borderRadius: '0px 0px 40px 40px',
    padding: '28px 32px 32px',
    zIndex: 20, // Keep it above the body
  },
  headerInner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '12px',
  },
  headerIcon: {
    width: '48px',
    height: '48px',
    backgroundColor: '#EFF6FF',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  title: {
    margin: 0,
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '28px',
    color: '#09253A',
  },
  subtitle: {
    margin: 0,
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#4A5565',
  },
  body: {
    flex: 1, // Take up remaining space
    overflowY: 'auto', // ONLY the body scrolls now
    padding: '24px 32px',
  },
  sectionsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  sectionTitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600,
    fontSize: '16px',
    color: '#09253A',
  },
  list: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    paddingLeft: '26px',
  },
  listItem: {
    display: 'flex',
    gap: '10px',
  },
  bullet: {
    color: '#26282B',
  },
  listText: {
    fontSize: '14px',
    color: '#26282B',
  },
  noticeBanner: {
    backgroundColor: '#FEF3C7',
    border: '1px solid #FBBF24',
    borderRadius: '10px',
    padding: '14px 28px',
  },
  noticeTop: {
    display: 'flex',
    gap: '12px',
  },
  noticeContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  noticeTitle: {
    margin: 0,
    fontWeight: 600,
    fontSize: '14px',
    color: '#92400E',
  },
  noticeText: {
    margin: 0,
    fontSize: '13px',
    color: '#78350F',
  },
  checkboxBanner: {
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '10px',
    padding: '17px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
  },
  checkbox: {
    width: 0,
    height: 0,
    position: 'absolute',
    opacity: 0,
  },
  checkboxText: {
    margin: 0,
    fontWeight: 500,
    fontSize: '14px',
    color: '#09253A',
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    padding: '16px 32px 32px',
    borderTop: '1px solid #E5E7EB',
    backgroundColor: '#FFFFFF',
  },
  cancelBtn: {
    padding: '10px 22px',
    height: '48px',
    border: '1px solid #4D8BF1',
    borderRadius: '43px',
    backgroundColor: '#FFFFFF',
    fontWeight: 600,
    cursor: 'pointer',
  },
  startBtn: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 22px',
    gap: '5px',
    height: '48px',
    border: 'none',
    backgroundColor: '#4D8BF1',
    borderRadius: '43px',
    fontWeight: 600,
    color: '#FFFFFF',
  },
};
